import { ENV } from '@forwardflow/helpers/configs'
import Icon from '@forwardflow/ui/Icons'
import { useSearchParams } from 'react-router-dom'
import {
   readMcpHandle,
   rememberMcpHandle,
} from '@forwardflow/services/McpContinuation'


interface SocialLoginProps {
   demoBookingId?: number
}

const SocialLogin: React.FC<SocialLoginProps> = ({ demoBookingId }) => {
   const [searchParams] = useSearchParams()
   const invitationToken = searchParams.get('invitationToken')

   /**
    * Stash the MCP handle before leaving for the provider.
    *
    * Google and Microsoft return to `window.location.origin` and drop every
    * query parameter, so the handle cannot ride the round trip in the URL. It
    * is read back on return in `Signin`.
    */
   const rememberContinuation = () => {
      const handle = readMcpHandle(searchParams)
      if (handle) rememberMcpHandle(handle)
   }

   const buildRedirectUrl = (provider: 'google' | 'microsoft') => {
      let baseUrl = `${ENV.PUBLIC_FF_URL}auth/${provider}?redirectUrl=${window.location.origin}`
      if (demoBookingId) {
         baseUrl += `&demoBookingId=${demoBookingId}`
      }
      if (invitationToken) {
         baseUrl += `&invitationToken=${invitationToken}`
      }

      return baseUrl
   }

   return (
      <div
         className="w-full max-w-[550px] rounded-lg"
         style={{
            background: 'linear-gradient(90deg, rgba(159, 123, 255, 0.1), rgba(235, 105, 194, 0.1))',
         }}
      >
         {/* Recommendation message */}
         <div className="flex items-center justify-between gap-2 mb-3 p-3 pb-0">
            <span className="text-xs md:text-sm text-white/60">
               No manual setup - your calendar connects instantly.
            </span>
            <span
               className="px-3 py-1 text-xs font-medium text-white rounded whitespace-nowrap"
               style={{
                  background: 'linear-gradient(90deg, rgba(159, 123, 255, 0.3), rgba(251, 100, 182, 0.3))',
               }}
            >
               Recommended
            </span>
         </div>

         {/* Google login button with purple-pink gradient border */}
         <div className="p-[2px] rounded-lg bg-gradient-to-r from-[#A684FF] to-[#EB69C2] hover:opacity-90 transition-all">
            <a
               className="rounded-[6px] group text-[14px] md:text-[18px] bg-black/40 h-[56px] md:h-[60px] w-full flexc relative hover:bg-black/50 transition"
               href={buildRedirectUrl('google')}
               onClick={rememberContinuation}
            >
               <Icon
                  name="google-icon"
                  className="absolute left-6 md:left-8 w-5 md:w-6"
               />
               <span className="text-white">Continue with Google</span>
            </a>
         </div>

         {/* Microsoft login button */}
         <div className="mt-3 p-[2px] rounded-lg bg-gradient-to-r from-[#A684FF] to-[#EB69C2] hover:opacity-90 transition-all">
            <a
               className="rounded-[6px] group text-[14px] md:text-[18px] bg-black/40 h-[56px] md:h-[60px] w-full flexc relative hover:bg-black/50 transition"
               href={buildRedirectUrl('microsoft')}
               onClick={rememberContinuation}
            >
               <Icon
                  name="microsoft-icon"
                  className="absolute left-6 md:left-8 w-5 md:w-6"
               />
               <span className="text-white">Continue with Microsoft</span>
            </a>
         </div>
      </div>
   )
}

export default SocialLogin
